import { useState, useEffect, useRef, useMemo } from 'react';
import { searchProviders } from '../lib/search-registry';
import type { SearchablePage } from '../lib/search-registry';
import { matchInput } from '../lib/search-patterns';
import type { PatternMatch } from '../lib/search-patterns';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { networkManager } from '../api/NetworkManager';

interface SearchIntent {
  intent: string;
  confidence: number;
  suggestions: Array<{
    label: string;
    description: string;
    route: string;
    iconName: string;
  }>;
}

interface UseSearchResult {
  patternMatches: PatternMatch[];
  pages: SearchablePage[];
  aiSuggestions: SearchIntent | null;
  isLoadingAI: boolean;
}

export function useSearch(query: string): UseSearchResult {
  const { can } = useAuth();
  const { environment } = useEnvironment();
  const [aiSuggestions, setAiSuggestions] = useState<SearchIntent | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Filter providers by user capabilities
  const accessibleProviders = useMemo(
    () => searchProviders.filter((p) => can(environment, p.capability.domain, p.capability.action)),
    [can, environment],
  );

  // Pattern matches (sync, no network)
  const patternMatches = useMemo(
    () => matchInput(query, accessibleProviders),
    [query, accessibleProviders],
  );

  // All accessible pages for fuzzy matching
  const pages = useMemo(() => accessibleProviders.flatMap((p) => p.pages), [accessibleProviders]);

  // Debounced AI intent detection
  useEffect(() => {
    const trimmed = query.trim();

    // Only trigger AI for natural language (>= 5 chars, no pattern match)
    if (trimmed.length < 5 || patternMatches.length > 0) {
      setAiSuggestions(null);
      setIsLoadingAI(false);
      return;
    }

    setIsLoadingAI(true);

    // Abort previous request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const timer = setTimeout(async () => {
      try {
        const response = await networkManager.post<{ status: boolean; data: SearchIntent }>(
          '/api/search/intent',
          { query: trimmed },
          { signal: controller.signal },
        );
        if (!controller.signal.aborted) {
          setAiSuggestions(response.data);
          setIsLoadingAI(false);
        }
      } catch {
        if (!controller.signal.aborted) {
          setAiSuggestions(null);
          setIsLoadingAI(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, patternMatches.length]);

  return { patternMatches, pages, aiSuggestions, isLoadingAI };
}
